// YouTubeImportDialog.js
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTitle, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CollectionService from "@/services/CollectionService";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import posthog from "posthog-js";
import { ReloadIcon } from "@radix-ui/react-icons";
import useStore from "@/store";
import languageNames from "@/constants/languageNames";

import "/node_modules/flag-icons/css/flag-icons.min.css";
import { DEFAULT_LEARNING_LANGUAGE, countryForLanguage } from "@/constants/langcraftLanguages";
import LanguagePicker from "@/components/LanguagePicker";

const YouTubeImportDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { setCurrentCollectionId } = useStore().user;
  const { setCurrentCollectionById } = useStore().collections;
  const [youTubeUrl, setYouTubeUrl] = useState("");
  const [numSubtitlesToMerge, setNumSubtitlesToMerge] = useState(1);
  const [title, setTitle] = useState("Untitled");
  const [language, setLanguage] = useState(DEFAULT_LEARNING_LANGUAGE);
  const [isCreating, setIsCreating] = useState(false);

  function mapLanguageToCountry(language) {
    console.log("Language:", language);
    // Default mapping for 'en' to 'gb'
    if (language === "en") {
      return "gb";
    }

    // Specific mappings for variations of English
    switch (language) {
      case "ja":
        return "jp";
      case "hi":
        return "in";
      case "en-us":
        return "us";
      case "en-gb":
      case "en-gb":
        return "gb";
      case "zh":
        return "cn";
      case "ar":
        return "sa";
      case "ko":
        return "kr";
      default:
        // Otherwise use the flag country from the LangCraft language list
        return countryForLanguage(language) || language.split("-")[0];
    }
  }

  const handleCreateCollection = async () => {
    try {
      setIsCreating(true);
      const collectionDetails = {
        title,
        language,
        youtubeUrl: youTubeUrl,
        type: "youtube",
        contents: [],
      };

      const createdCollection =
        await CollectionService.createCollectionWithContents(
          collectionDetails.title,
          collectionDetails.language,
          collectionDetails.contents,
          collectionDetails.type,
          collectionDetails.youtubeUrl,
          numSubtitlesToMerge
        );

      toast("Success", {
        description: "The collection was created successfully!",
        action: {
          label: "Close",
          onClick: () => console.log("Close"),
        },
      });
      posthog.capture("collection_created");
      await setCurrentCollectionById(createdCollection.id);
      await setCurrentCollectionId(createdCollection.id);
      navigate("/");
    } catch (error) {
      console.error("Failed to create collection:", error);
      toast("Error", {
        description: `Failed to create collection: ${error.response.data.error}`,
        action: {
          label: "Close",
          onClick: () => console.log("Error Close"),
        },
      });
    } finally {
      setIsCreating(false);
      setYouTubeUrl("");
      setNumSubtitlesToMerge(1);
      setTitle("Untitled");
      setLanguage("en");
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl rounded-[28px] border border-line bg-surface p-6 text-fg shadow-panel backdrop-blur-2xl">
        <div className="space-y-6">
          <div className="space-y-2">
            <DialogTitle className="text-2xl font-semibold tracking-tight text-fg">
              Import From YouTube
            </DialogTitle>
            <p className="text-sm text-fg-muted">
              Bring in subtitles directly from a YouTube video.
            </p>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label
                htmlFor="title"
                className="text-sm font-medium text-fg-muted"
              >
                Title
              </Label>
              <Input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter title"
                className="h-12 w-full rounded-2xl border border-line bg-surface/80 px-4 text-sm text-fg placeholder:text-fg-subtle transition focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="youtube-url"
                className="text-sm font-medium text-fg-muted"
              >
                YouTube URL
              </Label>
              <Input
                id="youtube-url"
                type="text"
                value={youTubeUrl}
                onChange={(e) => setYouTubeUrl(e.target.value)}
                placeholder="Enter YouTube video URL"
                className="h-12 w-full rounded-2xl border border-line bg-surface/80 px-4 text-sm text-fg placeholder:text-fg-subtle transition focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="language"
                className="text-sm font-medium text-fg-muted"
              >
                Language
              </Label>
              <LanguagePicker
                id="language"
                value={language}
                onChange={setLanguage}
                triggerClassName="h-12 rounded-2xl bg-surface/80 px-4"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="num-subtitles"
                className="text-sm font-medium text-fg-muted"
              >
                Number of Subtitles to Merge
              </Label>
              <Input
                id="num-subtitles"
                type="number"
                value={numSubtitlesToMerge}
                onChange={(e) =>
                  setNumSubtitlesToMerge(parseInt(e.target.value))
                }
                min={1}
                className="h-12 w-full rounded-2xl border border-line bg-surface/80 px-4 text-sm text-fg placeholder:text-fg-subtle transition focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
              />
            </div>
          </div>

          <div className="flex justify-center pt-2">
            <Button
              className="flex w-full max-w-[200px] items-center justify-center rounded-full border border-primary/50 bg-gradient-to-r from-primary to-primary px-6 py-3 text-sm font-semibold text-primary-fg shadow-panel transition hover:shadow-glow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
              onClick={handleCreateCollection}
              disabled={isCreating}
            >
              {isCreating ? (
                <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                "Create"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default YouTubeImportDialog;
